/**
 * Render-time localization for the default names the Director assigns to new
 * nodes ("Panel 1", "Scene 2", "Video Group 1", ...).
 *
 * The stored `in_name` / `in_label` stays canonical ENGLISH on purpose. Three
 * backend behaviours re-read it and would break if it moved (see
 * `agentic_workflow/core/default_node_names.py`):
 *   1. `_next_container_ordinal` derives the next number by re-parsing labels.
 *   2. `resolve_name_mentions` indexes nodes by display name.
 *   3. Director prompts quote these names.
 *
 * So we translate on the way OUT only, and never write a localized value back.
 *
 * Renames are respected without any rename hook. A marked node is localized
 * only while its stored value still equals the canonical rendering; the instant
 * a user renames it the value diverges, the marker goes inert, and the raw
 * string is shown. The divergence IS the signal — there is no marker to clear
 * and therefore no stale-state bug.
 */

import {
  DEFAULT_NODE_NAMES,
  FE_TYPE_TO_BACKEND,
  NAME_AUTOGEN_FIELD,
  type DefaultNodeName,
} from '../../../../generated/nodeDataTypes'

/**
 * Node data as this module consumes it: a bag of display fields we only ever
 * read by key and narrow with `typeof === 'string'`.
 *
 * Spelled out rather than `Record<string, unknown>` because
 * `check-forbidden-ts-syntax` bans the `unknown` keyword and this file is not
 * in its CAST_ALLOWLIST.
 */
type NodeDataFields = {
  readonly [key: string]: string | number | boolean | object | null | undefined
}

/**
 * Minimal translator shape — only what this module calls. Narrower than
 * i18next's overloaded `TFunction`, which keeps callers (and tests) from
 * needing a cast to satisfy it. Same precedent as `localizeNodeBgColor`.
 */
type OrdinalTranslator = (key: string, opts?: { n?: number }) => string

/** Families that can apply to a given backend node type. */
const BY_BACKEND_TYPE = DEFAULT_NODE_NAMES.reduce<Record<string, DefaultNodeName[]>>(
  (acc, entry) => {
    const bucket = acc[entry.nodeType] ?? []
    bucket.push(entry)
    acc[entry.nodeType] = bucket
    return acc
  },
  {},
)

/** Longest prefix first, so "Grid Video 1" is never mistaken for "Grid" + "Video 1". */
function byPrefixLengthDesc(a: DefaultNodeName, b: DefaultNodeName): number {
  return b.prefix.length - a.prefix.length
}

function ordinalFor(entry: DefaultNodeName, storedName: string): number | undefined {
  const match = new RegExp(`^${entry.prefix} (\\d+)$`).exec(storedName)
  return match ? Number(match[1]) : undefined
}

/**
 * Localize an auto-generated default node name.
 *
 * @param feType   ReactFlow node type (camelCase), e.g. `imageGenerator`.
 * @param data     Node data holding the display field and (maybe) the marker.
 * @param t        `canvas` namespace translator.
 * @returns The localized name, or `undefined` when this is not a recognised
 *          auto-generated default (user-named node, renamed node, unknown type)
 *          — callers then render the stored string verbatim.
 */
export function localizeDefaultNodeName(
  feType: string | null | undefined,
  data: NodeDataFields | null | undefined,
  t: OrdinalTranslator,
): string | undefined {
  if (!feType || !data) return undefined
  const backendType = FE_TYPE_TO_BACKEND[feType] ?? feType
  const candidates = BY_BACKEND_TYPE[backendType]
  if (!candidates) return undefined

  const marker = data[NAME_AUTOGEN_FIELD]

  if (typeof marker === 'string' && marker) {
    // Marked node: exact semantics. Localize only while the stored value still
    // matches this marker's canonical rendering.
    const entry = candidates.find((candidate) => candidate.key === marker)
    if (!entry) return undefined
    const stored = data[entry.field]
    if (typeof stored !== 'string') return undefined
    const ordinal = ordinalFor(entry, stored)
    if (ordinal === undefined) return undefined
    return t(`defaultNodeNames.${entry.key}`, { n: ordinal })
  }

  // Unmarked node — created before the marker existed. Best-effort: match the
  // canonical prefix pattern. Nothing is written back, so a false positive here
  // is display-only and self-corrects the first time the node is renamed.
  for (const entry of [...candidates].sort(byPrefixLengthDesc)) {
    const stored = data[entry.field]
    if (typeof stored !== 'string') continue
    const ordinal = ordinalFor(entry, stored)
    if (ordinal !== undefined) return t(`defaultNodeNames.${entry.key}`, { n: ordinal })
  }
  return undefined
}
